/**
 * Family / household read surface (Plan 06).
 *
 * Reads the owner User + their HOUSEHOLD AccountMembers + per-member
 * memberships (with the school Location they're enrolled at). Used by:
 *   - /my-family — direct render of the household
 *   - /orders/family — `listOrderingStudents` projection
 *   - /orders — `distinctSchoolLocations` projection
 *
 * Co-owner invites + add-student persistence land in upstream plans
 * (Plan 10 + Plan 36).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { UserAvatarColor } from '@/lib/user-avatar-colors'
import { useProfileStore } from '@/stores/profile'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MyFamilyMembership {
  id: string
  profileCode: string
  organizationId: string
  isArchived: boolean
  locationId?: string | null
  location?: {
    id: string
    name: string
    timeZone: string
    type: string
    /**
     * Order-deadline policy. FIXED_WEEKLY uses dayOfWeek/weeksBefore (null
     * day-of-week ⇒ no deadline); RELATIVE_DAYS uses relativeDays.
     */
    orderDeadlineMode?: 'FIXED_WEEKLY' | 'RELATIVE_DAYS' | null
    orderDeadlineDayOfWeek?: string | null
    orderDeadlineWeeksBefore?: number | null
    orderDeadlineTime?: string | null
    orderDeadlineRelativeDays?: number | null
    /**
     * Does this campus take parent orders? False = school ordering only
     * (PROD-1554): the family surfaces show it view-only. Absent (an older
     * payload) reads as true. Server: `parentOrderingAllowed`.
     */
    parentOrderingEnabled?: boolean
  } | null
}

/** Per-term Enrollment facts for a student (grade + homeroom). */
export interface MyFamilyEnrollment {
  termId: string
  locationId: string
  locationName: string
  gradeLevel: string | null
  gradeRange: {
    id: string
    code: string
    name: string
  } | null
  homeroom: string | null
}

export interface MyFamilyMealPlanEnrollment {
  id: string
  mealPlanId: string
  subsidyKind: string
  startDate: string | null
  endDate: string | null
  mealPlan: {
    id: string
    name: string
  }
}

export interface MyFamilyTag {
  id: string
  name: string
  slug: string
  kind: 'ALLERGEN' | 'DIETARY'
}

/** The five assignable enrollment fact dimensions (server vocabulary). */
export type EnrollmentFactDimension = 'GRADE' | 'TEACHER' | 'ROOM' | 'HOMEROOM' | 'LUNCH_PERIOD'

/** Active-term enrollment fact values for a student (null = not set). */
export interface StudentFactValues {
  grade: string | null
  teacher: string | null
  roomNumber: string | null
  homeroom: string | null
  lunchPeriod: string | null
}

/** Parent-facing labels for the fact dimensions (ROOM reads as Classroom). */
export const ENROLLMENT_FACT_LABELS: Record<EnrollmentFactDimension, string> = {
  GRADE: 'Grade',
  TEACHER: 'Teacher',
  ROOM: 'Classroom',
  HOMEROOM: 'Homeroom',
  LUNCH_PERIOD: 'Lunch period',
}

export interface MyFamilyMemberUser {
  id: string
  firstName: string
  lastName: string
  preferredName: string | null
  avatarUri: string | null
  avatarColor: UserAvatarColor
  email: string | null
  canSignIn: boolean
  birthday: string | null
  /**
   * Plan 49 — Ordo-minted student identifier. Format
   * `{3-CHAR_PREFIX}-{6-CHAR_BASE32}` (e.g. `LIN-X7K9P3`). Null for
   * members who haven't been minted one yet (parents, teachers, or
   * students that pre-date the manager + haven't been backfilled).
   * Parent-of-student can see this; the family card surfaces it
   * with a copy-to-clipboard affordance + a "use this at the
   * cafeteria" info chip.
   */
  ordoStudentId: string | null
  allergenTags: MyFamilyTag[]
  dietaryTags: MyFamilyTag[]
  medicalDocumentationAvailable: boolean
  memberships: MyFamilyMembership[]
  enrollments: MyFamilyEnrollment[]
  mealPlanEnrollments: MyFamilyMealPlanEnrollment[]
  /**
   * Claim-time gap-fill (optional for older fixtures — the live endpoint
   * always sends both). `facts` are the active-term enrollment values;
   * `missingRequiredFacts` are the campus-REQUIRED, askable dimensions the
   * parent still needs to fill — ordering for the student is blocked
   * server-side while it's non-empty.
   */
  facts?: StudentFactValues
  missingRequiredFacts?: EnrollmentFactDimension[]
  /**
   * The student's campus needs a human pick/confirm (no campus at a live
   * org, or an auto-assigned campus at a multi-campus org). Order-blocking
   * server-side, like missing required facts.
   */
  needsCampusConfirmation?: boolean
  /**
   * School-spending (POS) context — present ONLY when the student's campus
   * resolves POS-enabled AND the viewer is a parent (server-authorized;
   * absence hides the Spending tab + card block entirely).
   */
  pos?: MyFamilyStudentPos
}

/** PROD-227 PR4 — per-student POS context, scoped to the projected household. */
export interface MyFamilyStudentPos {
  campusPosEnabled: boolean
  /** Org negative-balance floor in cents (the "−$15" in the info alert). */
  negativeFloorCents: number
  /**
   * PROD-1119 — the school turned POS on for every student, so the switch
   * reads On by default and turning it off is an opt-OUT. Optional for
   * older fixtures.
   */
  campusDefaultOptIn?: boolean
  /** Daily cap a student inherits when nobody set one (cents). */
  defaultDailyLimitCents?: number
  /** The student lives in more than one household (payer designation applies). */
  dualHousehold: boolean
  /** This household is the student's primary (billing) household. */
  viewerIsPrimaryHousehold: boolean
  /** The viewer is a PRIMARY_GUARDIAN here (or ORDO_ADMIN) — may set the payer. */
  viewerIsPrimaryGuardian: boolean
  /** Which household register charges land on today. */
  payerHousehold: 'PRIMARY' | 'ALTERNATE'
  /** Display name of the non-primary household (dual-household students). */
  alternateHouseholdName: string | null
  /** The alternate household has POS on — required before it can be payer. */
  alternateOptedIn: boolean
}

export interface MyFamilyMember {
  id: string
  relationship: string
  isArchived: boolean
  /**
   * THIS household's POS wallet settings for the member (DQ3 — the row the
   * Spending tab edits). Optional for older fixtures. `posOptIn` is the
   * EFFECTIVE value (PROD-1119): the parent's own switch, or the school's
   * default opt-in with no parent opt-out.
   */
  posOptIn?: boolean
  spendingLimitCents?: number | null
  posPayer?: boolean
  user: MyFamilyMemberUser | null
}

export interface MyFamilyAccount {
  id: string
  kind: 'HOUSEHOLD' | 'ORGANIZATION'
  ownerUserId: string | null
  displayName: string | null
  isArchived: boolean
  /**
   * How the VIEWER relates to this household. 'PARENT' = full household
   * access (owner / co-parent / guardian). 'STUDENT' = the viewer is a
   * student with a granted sign-in — the server projects members
   * self-only and the client hides parent-only affordances (invite
   * co-parent, add student, balance management). Optional for older
   * fixtures — the live endpoint always sends it.
   */
  viewerRole?: 'PARENT' | 'STUDENT'
  /**
   * Payment-failure escalation (2026-08-06): non-null while a payment
   * failure on the household is uncorrected. `lockedOut` = the failure is
   * 13+ days old and NEW ordering is blocked until billing is fixed
   * (banner: `PaymentFailureBanner`). Parent viewers only; optional for
   * older fixtures.
   */
  paymentFailure?: {
    since: string
    lockoutAt: string | null
    lockedOut: boolean
  } | null
  /**
   * Ordering block (2026-08-25): non-null while this household is blocked from
   * ordering — either by an unresolved bank dispute (`source: 'STRIPE_DISPUTE'`)
   * or by support, by hand (`source: 'MANUAL'`). Ordering stops IMMEDIATELY (no
   * grace period) and the household's auto-orders are PAUSED — never cancelled,
   * so lifting the block restores them.
   *
   * `message` is the server's single-source copy naming the cure, and differs
   * per source: a manual block never claims a bank dispute. Render it as-is
   * (banner: `DisputeBlockBanner`). Parent viewers only; optional for older
   * fixtures, where `source` is absent.
   */
  disputeBlock?: {
    since: string
    disputeRef: string | null
    source?: 'STRIPE_DISPUTE' | 'MANUAL' | null
    message: string
  } | null
  members: MyFamilyMember[]
}

/** First (deterministic) unresolved payment failure across households. */
export function selectPaymentFailure(
  response: MyFamilyResponse | undefined,
): NonNullable<MyFamilyAccount['paymentFailure']> | null {
  for (const account of response?.me?.ownedAccounts ?? []) {
    if (account.kind === 'HOUSEHOLD' && !account.isArchived && account.paymentFailure) {
      return account.paymentFailure
    }
  }
  return null
}

/** First (deterministic) live ordering block across households. */
export function selectDisputeBlock(
  response: MyFamilyResponse | undefined,
): NonNullable<MyFamilyAccount['disputeBlock']> | null {
  for (const account of response?.me?.ownedAccounts ?? []) {
    if (account.kind === 'HOUSEHOLD' && !account.isArchived && account.disputeBlock) {
      return account.disputeBlock
    }
  }
  return null
}

export interface MyFamilyOwner {
  id: string
  firstName: string
  lastName: string
  preferredName: string | null
  avatarUri: string | null
  avatarColor: UserAvatarColor
  email: string | null
  canSignIn: boolean
}

/** A school-code / student claim still waiting on school verification. */
export interface MyFamilyPendingClaim {
  id: string
  requestedAt: string
  /** Known when the school matched the request to an enrolled student. */
  studentName: string | null
  schoolName: string
  locationName: string | null
}

export interface MyFamilyResponse {
  me:
    | (MyFamilyOwner & {
        /** Optional for older fixtures — the live endpoint always sends it. */
        pendingClaims?: MyFamilyPendingClaim[]
        ownedAccounts: MyFamilyAccount[]
      })
    | null
}

// Tag catalog (allergens / dietary)
export type TagKind = 'DIETARY' | 'ALLERGEN' | 'FLAVOR' | 'MARKETING' | 'MENU_SLOT'

export interface TagsByKindResponse {
  tags: Array<{
    id: string
    name: string
    slug: string
    kind: TagKind
  }>
}

export interface ClaimStudentInput {
  organizationId: string
  schoolLocationId?: string | null
  homeroom?: string | null
}

export interface ClaimStudentResponse {
  outcome: 'CLAIMED' | 'NO_MATCH' | 'PENDING_REVIEW'
  proofKind: 'PARENT_EMAIL' | 'LOCAL_STUDENT_ID' | 'ACTIVATION_CODE' | 'MANUAL_REVIEW'
  claimedStudents: Array<{
    userId: string
    accountId: string
    organizationId: string
    schoolLocationId: string | null
  }>
  reviewRequestIds: string[]
}

export interface ClaimActivationCodeInput {
  activationCode: string
}

export type ClaimActivationCodeResponse = ClaimStudentResponse

export interface FamilyClaimSchoolsResponse {
  schools: Array<{
    id: string
    name: string
    locations: Array<{ id: string; name: string }>
  }>
}

/** Grade + homeroom value lists a family can pick from (campus vocabulary). */
export interface FamilyEnrollmentOptionsResponse {
  termId: string | null
  /** The school's configured grade list, in vocabulary order. */
  gradeOptions: string[]
  /** The school's configured homeroom list, in vocabulary order. */
  homeroomOptions: string[]
  /**
   * The campus's full non-HIDDEN enrollment-fact form (empty stored values —
   * location-scoped, works before the student's membership carries the
   * campus). Optional for cached pre-upgrade responses.
   */
  factForm?: {
    location: { id: string; name: string } | null
    termId: string | null
    facts: StudentEnrollmentFormFact[]
    gradeLunchPeriods: Array<{ gradeLevel: string; lunchPeriod: string }>
  }
}

/** One pickable fact on the claim-time completion form. */
export interface StudentEnrollmentFormFact {
  fact: EnrollmentFactDimension
  label: string
  required: boolean
  /** REQUIRED + askable + not set — what the parent must fill. */
  missing: boolean
  /** False when the campus has no values to pick from (stays school-side). */
  askable: boolean
  options: string[]
  /** Stored value, or (lunch period only) the grade→lunch lookup result. */
  prefill: string | null
}

export interface StudentEnrollmentFormResponse {
  form: {
    location: { id: string; name: string } | null
    termId: string | null
    facts: StudentEnrollmentFormFact[]
    /** Whole lookup so the client re-derives lunch when the parent picks a grade. */
    gradeLunchPeriods: Array<{ gradeLevel: string; lunchPeriod: string }>
  }
  /** Campus pick/confirm block (optional for cached pre-upgrade responses). */
  campusConfirmation?: {
    required: boolean
    reason: 'no_campus' | 'unconfirmed_auto' | null
    organizationId: string | null
    currentLocationId: string | null
    campuses: Array<{ id: string; name: string }>
  }
}

export interface UpdateStudentEnrollmentInput {
  studentUserId: string
  /** Confirm/pick the student's campus (stamps the membership human-confirmed). */
  confirmLocationId?: string
  grade?: string
  teacher?: string
  roomNumber?: string
  homeroom?: string
  lunchPeriod?: string
}

export interface UpdateFamilyStudentInput {
  studentUserId: string
  organizationId?: string
  schoolLocationId?: string
  firstName?: string
  lastName?: string
  preferredName?: string | null
  avatarColor?: UserAvatarColor
  birthMonth?: string | null
  birthDay?: string | null
  homeroom?: string | null
  gradeLevel?: string
  teacher?: string
  roomNumber?: string
  lunchPeriod?: string
  allergenTagIds?: string[]
  dietaryTagIds?: string[]
  /** POS spending fields (PROD-227 PR4) — sparse; server-gated on effective POS. */
  posOptIn?: boolean
  spendingLimitCents?: number | null
  posPayer?: 'PRIMARY' | 'ALTERNATE'
}

export interface AddFamilyStudentInput {
  firstName: string
  lastName: string
  preferredName?: string | null
  organizationId: string
  schoolLocationId?: string | null
  birthMonth?: string | null
  birthDay?: string | null
  homeroom?: string | null
  gradeLevel?: string | null
  allergenTagIds?: string[]
  dietaryTagIds?: string[]
  createSignInAccount?: boolean
  email?: string | null
  phone?: string | null
  /**
   * Optional school-issued student id (`ordoStudentId`, e.g. `ROS-525UB2`).
   * Matching signal only — the server never stamps it on a new student.
   */
  studentId?: string | null
  /**
   * Explicit "Create anyway" acknowledgement after a `possible_duplicate`
   * 409 — the server skips the duplicate guard and creates normally.
   */
  confirmDuplicate?: boolean
  /**
   * Accept a `possible_duplicate` SUGGEST: link this existing household
   * student instead of creating one. Re-verified server-side.
   */
  attachExistingStudentUserId?: string | null
}

/**
 * Carried on a `possible_duplicate` 409 when the match is a same-household
 * child under a slightly different name ("Bradly" vs "Bradly Gael") — the
 * parent picks "Link" or "Create anyway".
 */
export interface DuplicateStudentSuggestion {
  studentUserId: string
  firstName: string | null
  lastName: string | null
  preferredName: string | null
  ordoStudentId: string | null
  gradeLevel: string | null
  /** True when the parent had removed this child from the household earlier. */
  removedFromHousehold: boolean
  basis: 'STUDENT_ID' | 'EXACT_NAME' | 'FUZZY_NAME'
}

export interface AddFamilyStudentResponse {
  student: {
    accountId: string
    accountMemberId: string
    studentUserId: string
  }
  /**
   * True when the server attached an existing student (this household's
   * kid, or an unclaimed household-less student) instead of creating one.
   */
  attachedExisting?: boolean
  /** How the attached student was matched; present with `attachedExisting`. */
  attachBasis?: 'STUDENT_ID' | 'EXACT_NAME' | 'FUZZY_NAME' | null
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchMyFamily(signal?: AbortSignal): Promise<MyFamilyResponse> {
  return apiFetch<MyFamilyResponse>('/api/family/me', { signal })
}

export async function fetchTagsByKind(kind: TagKind, signal?: AbortSignal): Promise<TagsByKindResponse> {
  return apiFetch<TagsByKindResponse>('/api/family/tags', { query: { kind }, signal })
}

export async function claimStudent(input: ClaimStudentInput): Promise<ClaimStudentResponse> {
  return apiFetch<ClaimStudentResponse>('/api/family/students/claim', {
    method: 'POST',
    body: input,
  })
}

export async function claimActivationCode(input: ClaimActivationCodeInput): Promise<ClaimActivationCodeResponse> {
  return apiFetch<ClaimActivationCodeResponse>('/api/family/activation-code/claim', {
    method: 'POST',
    body: input,
  })
}

export async function addFamilyStudent(input: AddFamilyStudentInput): Promise<AddFamilyStudentResponse> {
  return apiFetch<AddFamilyStudentResponse>('/api/family/students', {
    method: 'POST',
    body: input,
  })
}

export async function fetchFamilyClaimSchools(signal?: AbortSignal): Promise<FamilyClaimSchoolsResponse> {
  return apiFetch<FamilyClaimSchoolsResponse>('/api/family/claim-schools', { signal })
}

export async function fetchFamilyEnrollmentOptions(
  input: { organizationId: string; schoolLocationId: string },
  signal?: AbortSignal,
): Promise<FamilyEnrollmentOptionsResponse> {
  return apiFetch<FamilyEnrollmentOptionsResponse>('/api/family/enrollment-options', { query: input, signal })
}

export async function fetchStudentEnrollmentForm(
  studentUserId: string,
  signal?: AbortSignal,
): Promise<StudentEnrollmentFormResponse> {
  return apiFetch<StudentEnrollmentFormResponse>(
    `/api/family/students/${encodeURIComponent(studentUserId)}/enrollment-form`,
    { signal },
  )
}

export async function updateStudentEnrollment(
  input: UpdateStudentEnrollmentInput,
): Promise<{ ok: true; facts?: StudentFactValues }> {
  const { studentUserId, ...body } = input
  return apiFetch<{ ok: true; facts?: StudentFactValues }>(
    `/api/family/students/${encodeURIComponent(studentUserId)}/enrollment`,
    { method: 'PATCH', body },
  )
}

export async function updateFamilyStudent(input: UpdateFamilyStudentInput): Promise<{ ok: true }> {
  const { studentUserId, ...body } = input
  return apiFetch<{ ok: true }>(`/api/family/students/${encodeURIComponent(studentUserId)}`, {
    method: 'PATCH',
    body,
  })
}

export interface RemoveFamilyStudentResponse {
  ok: true
  /** Non-archived dependents left in the household after this removal. */
  remainingDependents: number
  /** True when the last dependent left and the owner reverted to staff-only. */
  revertedToStaffOnly: boolean
}

export async function removeFamilyStudent(studentUserId: string): Promise<RemoveFamilyStudentResponse> {
  return apiFetch<RemoveFamilyStudentResponse>(`/api/family/students/${encodeURIComponent(studentUserId)}`, {
    method: 'DELETE',
  })
}

export interface GrantStudentSignInInput {
  studentUserId: string
  email: string
}

/**
 * Grant a sign-in to an EXISTING student: multipass account + linkage.
 * Once signed in, the student can browse the menu and place self-only
 * orders that charge the household. 409 `email_in_use` when the email
 * already belongs to another sign-in account.
 */
export async function grantStudentSignIn(input: GrantStudentSignInInput): Promise<{ ok: true }> {
  const { studentUserId, email } = input
  return apiFetch<{ ok: true }>(`/api/family/students/${encodeURIComponent(studentUserId)}/sign-in`, {
    method: 'POST',
    body: { email },
  })
}

export async function revokeStudentSignIn(studentUserId: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/family/students/${encodeURIComponent(studentUserId)}/sign-in`, {
    method: 'DELETE',
  })
}

export interface EnableSelfOrderingResponse {
  accountId: string
}

/**
 * Provision the signed-in staff member (teacher) as their own diner: ensures
 * they own a household and are a member of it, without granting a PARENT
 * profile. Idempotent — safe to call on every Orders visit.
 */
export async function enableSelfOrdering(): Promise<EnableSelfOrderingResponse> {
  return apiFetch<EnableSelfOrderingResponse>('/api/family/self', { method: 'POST' })
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const myFamilyQueryKey = ['family', 'me'] as const

export function useMyFamilyQuery() {
  return useQuery<MyFamilyResponse>({
    queryKey: myFamilyQueryKey,
    queryFn: ({ signal }) => fetchMyFamily(signal),
  })
}

export function useTagsByKindQuery(kind: TagKind) {
  return useQuery<TagsByKindResponse>({
    queryKey: ['family', 'tags', kind],
    queryFn: ({ signal }) => fetchTagsByKind(kind, signal),
    // Tag catalogs are seed-driven + change rarely; cache across the
    // session aggressively so the multi-selects render without a delay.
    staleTime: 10 * 60 * 1000,
  })
}

export function useClaimStudentMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: claimStudent,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: myFamilyQueryKey })
      // Claiming the first student grants the actor a PARENT profile — refresh
      // so nav ("My Family") + Orders variant reflect Teacher → Teacher+Parent.
      await useProfileStore.getState().refresh()
    },
  })
}

export function useClaimActivationCodeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: claimActivationCode,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: myFamilyQueryKey })
      await useProfileStore.getState().refresh()
    },
  })
}

export function useAddFamilyStudentMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: addFamilyStudent,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: myFamilyQueryKey })
      // Adding the first student grants the actor a PARENT profile — refresh
      // so nav ("My Family") + Orders variant reflect Teacher → Teacher+Parent.
      await useProfileStore.getState().refresh()
    },
  })
}

export function useFamilyClaimSchoolsQuery(options?: { enabled?: boolean }) {
  return useQuery<FamilyClaimSchoolsResponse>({
    queryKey: ['family', 'claim-schools'],
    queryFn: ({ signal }) => fetchFamilyClaimSchools(signal),
    staleTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export function useFamilyEnrollmentOptionsQuery(input: { organizationId: string; schoolLocationId: string } | null) {
  return useQuery<FamilyEnrollmentOptionsResponse>({
    queryKey: ['family', 'enrollment-options', input?.organizationId ?? '', input?.schoolLocationId ?? ''],
    queryFn: ({ signal }) => fetchFamilyEnrollmentOptions(input!, signal),
    enabled: Boolean(input?.organizationId && input?.schoolLocationId),
    staleTime: 10 * 60 * 1000,
  })
}

export const studentEnrollmentFormQueryKey = (studentUserId: string) =>
  ['family', 'student-enrollment-form', studentUserId] as const

export function useStudentEnrollmentFormQuery(studentUserId: string | null) {
  return useQuery<StudentEnrollmentFormResponse>({
    queryKey: studentEnrollmentFormQueryKey(studentUserId ?? ''),
    queryFn: ({ signal }) => fetchStudentEnrollmentForm(studentUserId!, signal),
    enabled: Boolean(studentUserId),
  })
}

export function useUpdateStudentEnrollmentMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateStudentEnrollment,
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: myFamilyQueryKey })
      await queryClient.invalidateQueries({ queryKey: studentEnrollmentFormQueryKey(variables.studentUserId) })
    },
  })
}

/** A household student the parent still needs to fill REQUIRED facts for. */
export interface StudentNeedingInfo {
  userId: string
  displayName: string
  missingRequiredFacts: EnrollmentFactDimension[]
  needsCampusConfirmation: boolean
}

/**
 * Students (across every accessible household) with campus-REQUIRED facts
 * still missing. Parent viewers only — a student with a granted sign-in
 * can't edit enrollment facts, so their households are skipped.
 */
export function listStudentsNeedingRequiredInfo(data: MyFamilyResponse | null | undefined): StudentNeedingInfo[] {
  const households = (data?.me?.ownedAccounts ?? []).filter(
    (account) => account.kind === 'HOUSEHOLD' && !account.isArchived && account.viewerRole !== 'STUDENT',
  )
  const seen = new Set<string>()
  const out: StudentNeedingInfo[] = []
  for (const household of households) {
    for (const member of household.members) {
      const user = member.user
      if (member.isArchived || !user || seen.has(user.id)) {
        continue
      }
      seen.add(user.id)
      const missing = user.missingRequiredFacts ?? []
      const needsCampusConfirmation = user.needsCampusConfirmation ?? false
      if (missing.length === 0 && !needsCampusConfirmation) {
        continue
      }
      out.push({
        userId: user.id,
        displayName: user.preferredName || user.firstName,
        missingRequiredFacts: missing,
        needsCampusConfirmation,
      })
    }
  }
  return out
}

/** Banner copy shared by /family and /orders/family: names the first missing fact. */
export function missingRequiredInfoMessage(students: StudentNeedingInfo[]): string {
  const first = students[0]
  if (!first) {
    return ''
  }
  if (students.length > 1) {
    return `${students.length} students need school info before you can order.`
  }
  if (first.needsCampusConfirmation) {
    return `${first.displayName} needs their school campus confirmed before you can order.`
  }
  const firstFact = first.missingRequiredFacts[0]
  const factLabel = firstFact ? ENROLLMENT_FACT_LABELS[firstFact].toLowerCase() : 'school info'
  return `${first.displayName} needs their ${factLabel} before you can order.`
}

export function useUpdateFamilyStudentMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateFamilyStudent,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: myFamilyQueryKey })
    },
  })
}

export function useEnableSelfOrderingMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: enableSelfOrdering,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: myFamilyQueryKey })
      // Provisioning doesn't change profileCodes today (no PARENT granted),
      // but refresh keeps nav/profile consistent if that ever changes.
      await useProfileStore.getState().refresh()
    },
  })
}

export function useGrantStudentSignInMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: grantStudentSignIn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: myFamilyQueryKey })
    },
  })
}

export function useRevokeStudentSignInMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: revokeStudentSignIn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: myFamilyQueryKey })
    },
  })
}

export function useRemoveFamilyStudentMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: removeFamilyStudent,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: myFamilyQueryKey })
      // Removing the last dependent may drop the PARENT profile (teacher →
      // teacher-only), so re-fetch profileCodes to update nav + Orders variant.
      await useProfileStore.getState().refresh()
    },
  })
}
